import React, { useEffect, useMemo, useState } from "react";

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={[
        "h-10 px-3 rounded-xl text-sm font-medium border transition",
        active
          ? "bg-white text-gray-900 border-gray-300 shadow-sm"
          : "bg-white/50 text-gray-600 border-white/40 hover:bg-white/70",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={[
        "rounded-2xl border border-white/60 bg-white/75 backdrop-blur-sm shadow-sm",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function CardHeader({ kicker, title }) {
  return (
    <div className="px-5 pt-5 pb-3">
      {kicker && <div className="text-xs text-gray-500">{kicker}</div>}
      {title && <div className="text-base font-bold text-gray-900">{title}</div>}
    </div>
  );
}

function CardBody({ children }) {
  return <div className="px-5 pb-5">{children}</div>;
}

function InfoRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-2 border-b border-gray-100 last:border-b-0">
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">{value}</div>
    </div>
  );
}

export default function ContextHelpModal({
  open,
  onClose,
  course,
  contextItem,
  initialTab = "overview",
  contained = false,
}) {
  const [tab, setTab] = useState(initialTab);

  const ipaSrc = contextItem?.ipa_image_filename
    ? `${process.env.REACT_APP_USER_API_URL}/static/ipa_images/${contextItem.ipa_image_filename}`
    : contextItem?.ipa_image_url;

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
  }, [open, initialTab, contextItem?.nos_id]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    if (!contained) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = prev;
        window.removeEventListener("keydown", onKey);
      };
    }

    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, contained]);

  const hasAbout =
    !!course?.AboutPanel &&
    !!course?.metadata?.help?.aboutEnabled;

  const title = useMemo(() => {
    if (!contextItem) return course?.metadata?.title || "Dialect info";
    return `${contextItem.place || "Dialect sample"}`;
  }, [contextItem, course?.metadata?.title]);

  const subtitle = useMemo(() => {
    if (!contextItem) return "";
    const parts = [contextItem.kommune, contextItem.fylke].filter(Boolean);
    return parts.join(" • ");
  }, [contextItem]);

  if (!open) return null;

  return (
    <div className={contained ? "absolute inset-0 z-[60]" : "fixed inset-0 z-[60]"}>
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onPointerDown={() => onClose?.()}
      />

      <div
        className={
          contained
            ? "absolute inset-0 flex items-stretch justify-stretch p-2 sm:p-3"
            : "absolute inset-0 flex items-center justify-center p-3 sm:p-4"
        }
        onPointerDown={() => onClose?.()}
      >
        <div
          onPointerDown={(e) => e.stopPropagation()}
          className={
            contained
              ? "w-full h-full bg-gradient-to-br from-blue-50 via-white to-purple-50 border border-white/60 shadow-2xl overflow-hidden rounded-2xl flex flex-col"
              : "w-full max-w-4xl bg-gradient-to-br from-blue-50 via-white to-purple-50 border border-white/60 shadow-2xl overflow-hidden rounded-none sm:rounded-2xl flex flex-col max-h-[100svh] sm:max-h-[90svh]"
          }
        >
          <div className="sticky top-0 z-20 bg-white/70 backdrop-blur-sm border-b border-white/60">
            <div className="px-5 py-4 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs text-gray-500">
                  {course?.metadata?.author || "Course context"}
                </div>
                <div className="text-lg sm:text-xl font-bold text-gray-900">
                  {title}
                </div>
                {subtitle && (
                  <div className="text-sm text-gray-600 mt-1">{subtitle}</div>
                )}
              </div>

              <button
                onClick={onClose}
                type="button"
                className="shrink-0 w-10 h-10 rounded-xl bg-white/70 border border-white/60 hover:bg-white flex items-center justify-center text-gray-700"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 pb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2 overflow-x-auto pb-1 pr-2">
                <TabButton active={tab === "overview"} onClick={() => setTab("overview")}>
                  Overview
                </TabButton>
                <TabButton active={tab === "transcription"} onClick={() => setTab("transcription")}>
                  Transcription
                </TabButton>
                <TabButton active={tab === "speaker"} onClick={() => setTab("speaker")}>
                  Speaker
                </TabButton>
                {hasAbout && (
                  <TabButton active={tab === "about"} onClick={() => setTab("about")}>
                    {course?.metadata?.help?.aboutLabel || "About"}
                  </TabButton>
                )}
              </div>
              <div className="text-xs text-gray-500">ESC closes • Click outside to close</div>
            </div>
          </div>

          <div className="p-5 sm:p-6 overflow-y-auto flex-1 min-h-0">
            {!contextItem ? (
              <Card>
                <CardBody>
                  <div className="text-center text-sm text-gray-600">
                    No dialect context available for this item.
                  </div>
                </CardBody>
              </Card>
            ) : (
              <>
                {tab === "overview" && (
                  <div className="space-y-4">
                    <Card>
                      <CardHeader kicker="LOCATION" title="Recording context" />
                      <CardBody>
                        <InfoRow label="Place" value={contextItem.place} />
                        <InfoRow label="Kommune" value={contextItem.kommune} />
                        <InfoRow label="Fylke" value={contextItem.fylke} />
                        <InfoRow label="Region" value={contextItem.tabu_region} />
                        <InfoRow label="Recording date" value={contextItem.recording_date} />
                      </CardBody>
                    </Card>

                    <Card>
                      <CardHeader kicker="DIALECT" title="Orthographic rendering" />
                      <CardBody>
                        <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                          {contextItem.orthographic || "No orthographic transcription available."}
                        </div>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardHeader kicker="SOURCE" title="Original entry" />
                      <CardBody>
                        <a
                          href={contextItem.detail_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800 break-all"
                        >
                          <span>Open original NTNU page</span>
                          <span aria-hidden="true">↗</span>
                        </a>
                      </CardBody>
                    </Card>
                  </div>
                )}

                {tab === "transcription" && (
                  <div className="space-y-4">
                    <Card>
                      <CardHeader kicker="XSAMPA" title="Phonetic transcription" />
                      <CardBody>
                        <div className="text-xs sm:text-sm font-mono text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
                          {contextItem.xsampa || "No XSAMPA available."}
                        </div>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardHeader kicker="IPA" title="IPA rendering" />
                      <CardBody>
                        {ipaSrc ? (
                          <img
                            src={ipaSrc}
                            alt={`IPA for ${contextItem.place || contextItem.nos_id || "dialect sample"}`}
                            className="max-w-full h-auto rounded-lg border border-gray-200 bg-white"
                          />
                        ) : (
                          <div className="text-sm text-gray-600">
                            No IPA image available.
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </div>
                )}

                {tab === "speaker" && (
                  <div className="space-y-4">
                    <Card>
                      <CardHeader kicker="INFORMANT" title="Speaker information" />
                      <CardBody>
                        <InfoRow label="Birth year" value={contextItem.informant?.["Fødselsår"]} />
                        <InfoRow label="Gender" value={contextItem.informant?.["Kjønn"]} />
                        <InfoRow label="Age to place" value={contextItem.informant?.["Alder til sted"]} />
                        <InfoRow label="Age from place" value={contextItem.informant?.["Alder fra sted"]} />
                        <InfoRow
                          label="Same dialect in family"
                          value={contextItem.informant?.["Foreldre/foresatte med samme dialekt"]}
                        />
                        <InfoRow
                          label="Representative"
                          value={contextItem.informant?.["Representativ dialektbruker (egenvurdering)"]}
                        />
                        <InfoRow label="Occupation" value={contextItem.informant?.["Yrke"]} />
                        <InfoRow label="Education" value={contextItem.informant?.["Utdanning"]} />
                      </CardBody>
                    </Card>

                    {contextItem.dialect_description && (
                      <Card>
                        <CardHeader kicker="SELF-DESCRIPTION" title="Dialect description" />
                        <CardBody>
                          <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                            {contextItem.dialect_description}
                          </div>
                        </CardBody>
                      </Card>
                    )}
                  </div>
                )}

                {tab === "about" && hasAbout && (
                  <div className="space-y-4">
                    <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
                      <CardHeader kicker="ABOUT" title="About this course" />
                      <CardBody>
                        {course?.AboutPanel ? <course.AboutPanel compact /> : null}
                      </CardBody>
                    </Card>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border-t border-white/60 bg-white/60 backdrop-blur-sm px-5 py-4 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              type="button"
              className="px-4 py-2 rounded-xl bg-white/80 border border-white/60 hover:bg-white text-sm font-medium text-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}